// The conditions table's column axis: which hours become columns, and how they
// group into days. Pure — no DOM, no clock of its own — so node can test the
// arithmetic that the whole table's honesty rests on.
//
// THREE-HOURLY IS LOAD-BEARING, not a sampling convenience. The table prints a
// NUMBER in every cell, and a number needs about 46 px of column to stay
// legible with a direction arrow beside it. At hourly spacing a week is 168
// columns; you would be scrolling past unreadable slivers. At three-hourly it
// is 56, each wide enough to read, and the forecast loses nothing a planner
// uses — nobody schedules a coring station to the hour seven days out.
//
// The columns are picked on the DISPLAY clock, not on the forecast's own
// labels. Flip the console to UTC and the table re-picks its columns at 00/03/
// 06 UTC rather than relabelling the site's 00/03/06 as 05/08/11. Both readings
// are truthful about the same instants; this one is the one a person can scan,
// because "the 3-hourly columns" should mean the same thing in whichever clock
// you are reading. That is why both functions take their clock as an argument
// instead of importing one: the caller passes ui/time.ts's displayParts, and a
// test passes whatever it likes.

/** A day heading spanning a contiguous run of columns. `first`/`last` are
 *  POSITIONS IN THE COLUMN LIST (inclusive), not indices into the forecast. */
export interface DayGroup {
  /** Stable identity for the day in the display clock, "2026-08-13". Grouping
   *  is by this key rather than by counting eight columns — see the DST note. */
  key: string;
  first: number;
  last: number;
}

/** Wall-clock parts the axis needs. Structurally the ClockParts of ui/time.ts. */
export interface AxisParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
}

/**
 * The indices of `instants` that become columns: every hour that lands on a
 * `stepH` boundary in the display clock.
 *
 * Divisibility, not "every third element". A forecast series does not always
 * begin at midnight (ours starts two days back at whatever hour the run
 * covers), and counting by threes from an arbitrary start would put the columns
 * at 01/04/07 on one point and 02/05/08 on the next — the same weather, a
 * different table. Anchoring on the clock means every point, every reload and
 * every zone shows its columns at the same times of day.
 *
 * Falls back to a fixed stride only when divisibility yields nothing usable,
 * which needs a series whose labels are not hourly at all. A table with two
 * columns is a degraded table; a table with none is a bug report.
 */
export function pickColumns(
  instants: readonly number[],
  partsOf: (t: number) => AxisParts,
  stepH = 3,
): number[] {
  const out: number[] = [];
  for (let i = 0; i < instants.length; i++) {
    const t = instants[i];
    if (!Number.isFinite(t)) continue;
    if (partsOf(t).hour % stepH === 0) out.push(i);
  }
  if (out.length >= 2) return out;
  const stride: number[] = [];
  for (let i = 0; i < instants.length; i += stepH) {
    if (Number.isFinite(instants[i])) stride.push(i);
  }
  return stride;
}

/** The display-clock day a column belongs to, "2026-08-13". */
export function dayKey(p: AxisParts): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/**
 * Group columns into day headings.
 *
 * By CALENDAR KEY, never by counting a fixed number of columns per day. On the
 * night a clock goes forward the day holds 23 hours and seven 3-hourly columns;
 * on the night it goes back, 25 hours and nine, with two of them reading the
 * same wall-clock hour. Counting eight would put the heading "Thursday 13" over
 * part of Friday twice a year, in exactly the two weeks when a mis-stated day
 * is most likely to be believed. Flipping the console to UTC regroups the same
 * columns under different headings for the same reason and by the same rule.
 */
export function groupByDay(
  columns: readonly number[],
  instants: readonly number[],
  partsOf: (t: number) => AxisParts,
): DayGroup[] {
  const groups: DayGroup[] = [];
  for (let c = 0; c < columns.length; c++) {
    const t = instants[columns[c]];
    if (!Number.isFinite(t)) continue;
    const key = dayKey(partsOf(t));
    const last = groups[groups.length - 1];
    if (last !== undefined && last.key === key) last.last = c;
    else groups.push({ key, first: c, last: c });
  }
  return groups;
}

/**
 * The column position nearest an instant, or null when the cursor sits outside
 * the table's span. The table's counterpart to time/cursor.ts's indexAtTime,
 * and it must behave the same way at the edges: a playhead past the end of the
 * series highlights NOTHING rather than pinning to the last column, or the
 * table would print three-hour-old numbers under a playhead that has run off
 * into a gap. Tolerance is half a column, so every instant inside the span
 * belongs to exactly one column.
 */
export function columnAtTime(
  columns: readonly number[],
  instants: readonly number[],
  t: number | null,
): number | null {
  if (t === null || columns.length === 0) return null;
  const first = instants[columns[0]];
  const last = instants[columns[columns.length - 1]];
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;
  const step =
    columns.length > 1 ? Math.abs(last - first) / (columns.length - 1) : 3 * 3_600_000;
  const tol = step / 2;
  if (t < first - tol || t > last + tol) return null;
  let best: number | null = null;
  let bestGap = Infinity;
  for (let c = 0; c < columns.length; c++) {
    const gap = Math.abs(instants[columns[c]] - t);
    if (gap < bestGap) {
      bestGap = gap;
      best = c;
    }
  }
  return best;
}
